// Section 17 step 6 and Section 21: record who received what. Memories (Section 5), the typed
// report entries (shortage, rationing-outcome, seed), the Ledger accumulator's rationing rows
// (who went without, person-seasons, shortage decisions) and saved orders. Stream G accumulates
// the rest of the Ledger from report entries (the seed corn from `seed eaten`); the rows written
// here must not be derived from the entries again.
import { ageGroupOf, isChild } from '../../core/queries';
import type {
  MemoryKind,
  NeedSatisfaction,
  Person,
  PersonId,
  PhaseContext,
  PriorityGroupId,
} from '../../core/types';
import { remember } from '../shared/memory';
import { EPS, type Rationed, type ResourcePlan } from './plan';

const SHORT_MEMORY: Record<Rationed, MemoryKind> = {
  food: 'fed-short',
  water: 'thirsty',
  warmth: 'cold',
};

/** Section 5 memory: fed short or nothing, thirsty, cold, fed in full while others were short. */
export function recordMemories(
  ctx: PhaseContext,
  people: readonly Person[],
  plans: readonly ResourcePlan[],
  received: Readonly<Record<Rationed, Readonly<Record<PersonId, number>>>>,
): void {
  const shortBelow = ctx.cfg.needs.shortThreshold;
  const turn = ctx.season.turn;
  const note = (
    id: PersonId,
    kind: MemoryKind,
    resource: Rationed,
    amount: number,
    otherId?: PersonId,
  ): void => {
    const target = ctx.draft.people[id];
    if (target === undefined) return;
    remember(ctx, target, {
      turn,
      kind,
      resource,
      amount,
      ...(otherId === undefined ? {} : { otherId }),
    });
  };
  for (const plan of plans) {
    const r = plan.resource;
    const got = received[r];
    let anyShort = false;
    for (const p of people) {
      if ((plan.demand[p.id] ?? 0) <= 0) continue;
      const share = got[p.id] ?? 1;
      if (share >= shortBelow) continue;
      anyShort = true;
      note(p.id, r === 'food' && share <= EPS ? 'fed-none' : SHORT_MEMORY[r], r, share);
    }
    if (r !== 'food' || !anyShort) continue;
    for (const p of people) {
      if ((plan.demand[p.id] ?? 0) > 0 && (plan.final[p.id] ?? 1) >= 1 - EPS)
        note(p.id, 'fed-full-while-others-short', r, 1);
    }
    // A child in the household fed short: every adult and elder of the household remembers it.
    const child = new Map<string, PersonId>();
    for (const p of people) {
      if (isChild(ctx.cfg, p) && (got[p.id] ?? 1) < shortBelow && !child.has(p.householdId))
        child.set(p.householdId, p.id);
    }
    for (const p of people) {
      const kid = child.get(p.householdId);
      if (kid !== undefined && !isChild(ctx.cfg, p))
        note(p.id, 'child-fed-short', r, got[kid] ?? 0, kid);
    }
  }
}

/** Groups with a member rationed short, in the order's ranking (unlisted groups after). */
export function shortGroups(
  ctx: PhaseContext,
  plan: ResourcePlan,
  short: ReadonlySet<PersonId>,
): PriorityGroupId[] {
  if (short.size === 0) return [];
  const ids = Object.keys(ctx.registry.priorityGroups) as PriorityGroupId[];
  const hit = ids.filter((g) =>
    ctx.registry.priorityGroups[g].members(ctx).some((id) => short.has(id)),
  );
  const rank = (g: PriorityGroupId): number => {
    const i = plan.order.indexOf(g);
    return i < 0 ? plan.order.length + ids.indexOf(g) : i;
  };
  return hit.sort((a, b) => rank(a) - rank(b));
}

/**
 * "Save this as a priority order": groups ranked by the mean share their members received,
 * keeping only groups fed differently from everyone else, with the catch-all in its place.
 */
export function orderFromShares(ctx: PhaseContext, plan: ResourcePlan): PriorityGroupId[] {
  const ids = Object.keys(ctx.registry.priorityGroups) as PriorityGroupId[];
  const meanOf = (members: readonly PersonId[]): number | null => {
    let given = 0;
    let need = 0;
    for (const id of members) {
      const d = plan.demand[id] ?? 0;
      given += (plan.final[id] ?? 1) * d;
      need += d;
    }
    return need > 0 ? given / need : null;
  };
  const everyone = meanOf(ctx.registry.priorityGroups['everyone-else'].members(ctx)) ?? 1;
  const rows: { id: PriorityGroupId; mean: number; rank: number }[] = [];
  for (const id of ids) {
    const mean = meanOf(ctx.registry.priorityGroups[id].members(ctx));
    if (mean === null) continue;
    if (id !== 'everyone-else' && Math.abs(mean - everyone) <= 1e-6) continue;
    const listed = plan.order.indexOf(id);
    rows.push({ id, mean, rank: listed < 0 ? plan.order.length + ids.indexOf(id) : listed });
  }
  return rows.sort((a, b) => b.mean - a.mean || a.rank - b.rank).map((r) => r.id);
}

/** Section 21 accumulator rows owned by rationing (live mode only). */
export function recordLedger(
  ctx: PhaseContext,
  people: readonly Person[],
  plans: readonly ResourcePlan[],
  needs: Readonly<Record<PersonId, NeedSatisfaction>>,
): void {
  const acc = ctx.draft.ledger;
  const shortBelow = ctx.cfg.needs.shortThreshold;
  for (const p of people) {
    const food = needs[p.id]?.food ?? 1;
    const group = ageGroupOf(ctx.cfg, p.ageSeasons);
    acc.personSeasons[group].total += 1;
    if (food >= shortBelow) continue;
    acc.personSeasons[group].short += 1;
    const rec = acc.wentWithout[p.id] ?? {
      personId: p.id,
      name: p.name,
      ageGroup: group,
      origin: p.origin,
      seasonsShort: 0,
      seasonsFedNone: 0,
    };
    rec.seasonsShort += 1;
    if (food <= EPS) rec.seasonsFedNone += 1;
    rec.ageGroup = group;
    acc.wentWithout[p.id] = rec;
  }
  // `seedEaten` is Stream G's: it is accumulated from the `seed eaten` entry (one writer).
  for (const plan of plans) {
    if (!plan.shortage) continue;
    acc.shortageDecisions.push({
      turn: ctx.season.turn,
      resource: plan.resource,
      shortfall: Math.max(0, plan.needed - plan.available),
      pinnedNone: plan.pinnedNone,
      byHand: plan.byHand,
    });
  }
}
